"use client";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Users, PersonStanding, Heart, Share2, Layers } from "lucide-react";
import BuyButton from "@/components/common/BuyButton";
import CartButton from "@/components/common/CartButton";
import { correctUrlString } from "@/utils/utils";
import { EVENT_CARD_DEFAULT_IMG, ORDER_TYPE, USER_ROLE } from "@/utils/enum";

export interface EventGroupAggregate {
  group_name: string;
  fees_category: string | null;
  participation: string | null;
  gender: string | null;
  grade_from: string | null;
  grade_to: string | null;
  age_min: number | null;
  age_max: number | null;
  min_price: number | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  registration_start_date?: string | null;
  event_count: number;
}

interface EventCardProps {
  item: any;
  pathname: string;
  aggregate?: EventGroupAggregate | null;
  aggregateLoading?: boolean;
  userRole?: string;
  cart: any[];
  paginatedEvents: any[];
  onRegister: () => void;
  onBulkUpload?: () => void;
  onAddToCart: () => void;
  onRemoveFromCart: () => void;
}

const formatPrice = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(value);

const EventCard = ({
  item,
  pathname,
  aggregate,
  aggregateLoading = false,
  userRole,
  cart,
  paginatedEvents,
  onRegister,
  onBulkUpload,
  onAddToCart,
  onRemoveFromCart,
}: EventCardProps) => {
  const imageSrc =
    correctUrlString(item?.image_link)[0] ||
    EVENT_CARD_DEFAULT_IMG[ORDER_TYPE.PUBLIC];

  // const [wished, setWished] = useState(false);

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const url =
      typeof window !== "undefined"
        ? `${window.location.origin}${pathname}`
        : pathname;
    try {
      if (typeof navigator !== "undefined" && (navigator as any).share) {
        await (navigator as any).share({ title: item?.group_name, url });
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      /* user dismissed share sheet — ignore */
    }
  };

  // Detail chips — same text style as before (icon + label + value)
  const details: { icon: JSX.Element; label: string; value: string }[] = [];

  // Event type — Group / Individual, from the event's classification (Category)
  if (item?.Category) {
    details.push({
      icon: <Layers size={15} strokeWidth={2} />,
      label: "Type",
      value: item.Category,
    });
  }
  if (aggregate?.participation) {
    details.push({
      icon: <Users size={15} strokeWidth={2} />,
      label: "Participation",
      value: aggregate.participation,
    });
  }
  if (aggregate?.gender) {
    details.push({
      icon: <PersonStanding size={16} strokeWidth={2} />,
      label: "Gender",
      value: aggregate.gender,
    });
  }

  return (
    <article className="viva-event-card">
      <Link href={pathname} className="viva-event-card__media d-block">
        <span className="viva-event-card__badge">{item?.["Sub Type"]}</span>
        <Image
          src={imageSrc}
          height={300}
          width={500}
          alt={item?.group_name || "Event image"}
          className="viva-event-card__img"
        />
      </Link>

      <div className="viva-event-card__body">
        {/* Title row + quick actions (wishlist / share) */}
        <div className="viva-event-card__head">
          <Link href={pathname} className="viva-event-card__title">
            {item?.group_name}
          </Link>
          <div className="viva-event-card__quick">
            {/* <button
              type="button"
              className={`viva-quick-btn${wished ? " is-on" : ""}`}
              aria-label="Add to wishlist"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setWished((w) => !w);
              }}
            >
              <Heart size={16} fill={wished ? "currentColor" : "none"} />
            </button> */}
            <button
              type="button"
              className="viva-quick-btn"
              aria-label="Share event"
              onClick={handleShare}
            >
              <Share2 size={16} />
            </button>
          </div>
        </div>

        {/* Detail chips — icon + label + value, same as before */}
        {aggregateLoading && !aggregate ? (
          <div className="viva-event-card__chips" aria-hidden="true">
            <span className="viva-chip viva-chip--skeleton" />
            <span className="viva-chip viva-chip--skeleton" />
            <span className="viva-chip viva-chip--skeleton" />
          </div>
        ) : details.length > 0 ? (
          <div className="viva-event-card__chips">
            {details.map((detail) => (
              <span
                key={detail.label}
                className="viva-chip"
                title={detail.label}
              >
                <span className="viva-chip__icon">{detail.icon}</span>
                <span className="viva-chip__text">
                  <span className="viva-chip__label">{detail.label}</span>
                  <span className="viva-chip__value">{detail.value}</span>
                </span>
              </span>
            ))}
          </div>
        ) : null}

        <div className="viva-event-card__footer">
          {/* Starting price — data driven, hidden when unavailable */}
          {aggregate?.min_price != null && aggregate.min_price > 0 ? (
            <div className="viva-event-card__price">
              <span className="viva-event-card__price-value">
                {formatPrice(aggregate.min_price)}
              </span>
              {(aggregate.event_count ?? 0) > 1 && (
                <span className="viva-event-card__price-onwards">onwards</span>
              )}
            </div>
          ) : (
            <div className="viva-event-card__price viva-event-card__price--empty" />
          )}

          <div className="viva-event-card__actions">
            <BuyButton
              label={item.inCart > 0 ? "Added To Cart" : "Register"}
              classNames="viva-btn viva-btn--primary flex-grow-1"
              onButtonClick={onRegister}
            />
            {(userRole === USER_ROLE.AGENT || userRole === USER_ROLE.ADMIN) && (
              <BuyButton
                label="Bulk Upload"
                classNames="viva-btn viva-btn--primary flex-grow-1"
                onButtonClick={onBulkUpload}
              />
            )}
            <CartButton
              item={item}
              cart={cart}
              paginatedEvents={paginatedEvents}
              handleRemoveFromCart={onRemoveFromCart}
              handleAddToCart={onAddToCart}
            />
          </div>
        </div>
      </div>
    </article>
  );
};

export default EventCard;
